import moment from 'moment'
import DurationInput from '@/components/common/duration-input/duration-input-tmpl'
import EventBus from '@/components/mixins/event-bus-mixin'
import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin'
import MediaValidityMixin from '@/components/mixins/media-validity-mixin'
import TagMenuItem from '@/components/common/tags/tags-tmpl'
import PopIdentifierItem from '@/components/common/pop-identifier/pop-identifier-tmpl'
import schedulePlaytimeMenuItem from './schedule-playtime-menu-item/schedule-playtime-menu-item-tmpl'
import PublishSelectionMenuItem from './publish-menu-item/publish-selection-menu-item-tmpl'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import _ from 'lodash'
import { hasPermissionToPublishToPlayer } from '@/utils/permissions-common'
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage'
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore'
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader'

/**
 * Strip UI-only and empty date/time fields from an array of playtime objects.
 * This is the canonical normalization rule applied whenever playtime data is
 * written into the form, so that the API payload is clean by default.
 * Does NOT touch _edit — that stays on playtimes while they live in the form.
 * @param {Array} playtimes
 * @returns {Array}
 */
export const normalizePlaytimes = (playtimes) => {
  const emptyValues = ['', undefined, null]
  const dateTimeProps = ['startDate', 'startTime', 'endDate', 'endTime']
  return playtimes.map((pt) => {
    const cleaned = { ...pt }
    delete cleaned.tag
    for (const prop of dateTimeProps) {
      if (emptyValues.includes(cleaned[prop])) {
        delete cleaned[prop]
      }
    }
    return cleaned
  })
}

export default {
  name: 'media-menu',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError',
    '$signedInUserInfo'
  ],
  components: { TagMenuItem, schedulePlaytimeMenuItem, PopIdentifierItem, DurationInput, PublishSelectionMenuItem },
  props: {
    accessorComponentProp: {
      type: String,
      default () { return 'mediaPage' }
    },
    hasNoTemplateProp: {
      type: Boolean,
      default () { return false }
    },
    mediaSelectionProp: {
      type: Array,
      default () { return [] }
    },
    mediaPropertiesToHideProp: {
      type: Array,
      default () { return [] }
    },
    editProjectCustomNameProp: {
      type: String,
      default () { return 'Edit' }
    },
    userSchedulesProp: {
      type: Array,
      default () { return [] }
    },
    confirmForceToDeleteMediaProp: {
      type: Object,
      default () { return {} }
    },
    triggerPropertyActionProp: {
      type: Object,
      default () { return {} }
    },
    formTabStyleProp: {
      type: Boolean,
      default () { return false }
    },
    isMediaAlertTypeProp: {
      type: Boolean,
      default () { return false }
    },
    tagsSummaryProp: {
      type: Array,
      default () { return [] }
    },
    popIdsSummaryProp: {
      type: Array,
      default () { return [] }
    },
    selectedTagsFromSliderProp: {
      type: Array,
      default () { return [] }
    },
    showFluidLayout: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    accessorComponentProp (newVal) {
      this.accessorComponent = newVal
    },
    hasNoTemplateProp (newVal) {
      this.hasNoTemplate = newVal
    },
    mediaSelectionProp: {
      handler (newSelection, prevSelection) {
        const accessorComponentAllowedToProcessMediaSelectionChanged = ['mediaPage', 'schedulePage', 'ledPage']
        if (accessorComponentAllowedToProcessMediaSelectionChanged.indexOf(this.accessorComponent) >= 0) {
          this.handleMediaSelection(newSelection)
          this.handlePopIdVisibility()
        }
        this.userSchedules = this.filterUserSchedules(this.userSchedulesProp)
      },
      deep: true
    },
    userSchedulesProp: {
      handler (newUserSchedules) {
        this.userSchedules = this.filterUserSchedules(newUserSchedules)
      },
      deep: true
    },
    confirmForceToDeleteMediaProp (newConfirmForceToDeleteMedia) {
      if (newConfirmForceToDeleteMedia.confirmed === true && newConfirmForceToDeleteMedia.showDialog === true) {
        this.handleForceDeleteMedia(newConfirmForceToDeleteMedia.items)
      }
    },
    triggerPropertyActionProp (newTriggerPropertyActionVal) {
      this.triggerPropertyAction = newTriggerPropertyActionVal
      this.handleTriggerPropertyAction()
    },
    isMediaAlertTypeProp (newVal) {
      this.isMediaAlertType = newVal
    },
    popIdsSummaryProp: {
      handler (newVal) {
        this.popIdsSummary = newVal
      },
      deep: true
    },
    tagsSummaryProp: {
      handler (newVal) {
        this.tagsSummary = newVal
      },
      deep: true
    },
    selectedTagsFromSliderProp: {
      handler (newVal) {
        this.selectedTagsFromSlider = newVal
      },
      deep: true
    },
    editedMediaProperties: {
      handler (newVal) {
        // Set Unsaved Changes
        if (newVal.length) {
          this.setUnsavedDataFlagIntoLocalstorage({ page: this.accessorComponent })
        }
      },
      deep: true
    }
  },
  setup () {
    const fileUploadStorage = useFileUploadStorage()
    const fileUploaderStore = useFileUploaderStore();

    return { fileUploadStorage, fileUploaderStore }
  },
  data () {
    return {
      hasNoTemplate: this.hasNoTemplateProp,
      accessorComponent: this.accessorComponentProp,
      error: '',
      editableMediaProperties: [],
      editedMediaProperties: [],
      enableEditProject: this.$currentUser.getRights().userCreate.editProject,
      enableScheduleChangeTarget: this.$currentUser.getRights().userSchedule.changeTarget,
      enableEditMediaDuration: this.$currentUser.getRights().userCreate.editMediaDuration,
      fetchingUserSchedules: false,
      hiddenMenuItems: [],
      mediaPropertiesToHide: this.mediaPropertiesToHideProp,
      triggerPropertyAction: this.triggerPropertyActionProp,
      mediaProperties: [
        { id: 'duration', name: this.$t('label.duration'), editable: false },
        { id: 'name', name: '', editable: false },
        { id: 'schedules', name: this.$t('label.player', 1), selected: false },
        { id: 'tags', name: this.$t('label.tag', 2), editable: false },
        { id: 'popIdentifier', name: this.$t('label.popIdentifier', 2), editable: false },
        { id: 'validity', name: '', editable: false },
        { id: 'editProject', name: this.$t('label.edit'), editable: false },
        { id: 'delete', name: '', editable: false }
      ],
      mediaPropertiesId: { duration: 'duration', name: 'name', tags: 'tags', popIdentifier: 'popIdentifier', schedules: 'schedules', validity: 'validity', editProject: 'editProject', delete: 'delete' },
      mediaPropertiesForm: {
        values: {
          name: '',
          duration: null,
          players: {},
          scheduleAlert: false,
          schedules: [],
          scheduleSelection: [],
          schedulePlaytimeTarget: [],
          playtimeItemsVisible: 'all',
          scheduleNewTarget: {},
          scheduleNewTargetOptions: [],
          scheduleNewTargetDropdownVisible: false,
          scheduleNewTargetIconVisible: false,
          validity: [],
          validityPresentation: 'NONE',
          tags: [],
          popId: null
        },
        options: {
          tag: []
        }
      },
      mediaSelection: this.mediaSelectionProp,
      mediaSelectionType: 'single',
      mediaSelectionAPIRequestFlag: [],
      userSchedules: this.userSchedulesProp,
      modifySchedule: {
        formValues: {
          startDateAndEndDate: [],
          type: 'daily',
          startTime: '',
          endTime: '',
          repeatDays: [],
          showDateRange: false,
          showTimeRange: false
        },
        options: {
          type: [{ value: 'daily', label: '24/7' }, { value: 'custom', label: this.$t('label.custom') }],
          days: { mon: this.$t('label.mon'), tue: this.$t('label.tue'), wed: this.$t('label.wed'), thu: this.$t('label.thu'), fri: this.$t('label.fri'), sat: this.$t('label.sat'), sun: this.$t('label.sun') }
        }
      },
      formTabStyle: this.formTabStyleProp,
      showCommonPropertiesTabIcon: true,
      showSchedulePropertyTabIcon: true,
      formTabOptions: { commonProperties: 'commonProperties', scheduleProperty: 'scheduleProperty' },
      formTabSelected: null,
      isMediaAlertType: this.isMediaAlertTypeProp,
      tagsSummary: this.tagsSummaryProp,
      popIdsSummary: this.popIdsSummaryProp,
      selectedTagsFromSlider: this.selectedTagsFromSliderProp,
      currentPlaytimes: null,
      isTagMenuItemVisible: false,
      showPopId: false,
      publishOption: [],
      smartSyncHelpLink: CONFIG.EXTERNAL_LINKS.smartSync,
      isDiscoveryAccount: this.$signedInUserInfo.activePlan.name === this.$utils.options.plans.DISCOVERY
    }
  },
  mixins: [MediaScheduleMixin, MediaValidityMixin, UnsavedDataMixin],
  computed: {
    defaultMediaDuration () {
      return this.isMediaAlertType
        ? this.$utils.options.mediaDuration.DEFAULT_ALERT_SEC
        : this.$utils.options.mediaDuration.DEFAULT_MEDIA_SEC
    },
    defaultMediaDurationFormatted () {
      return this.$utils.formatDurationToMinutesAndSeconds({ duration: this.defaultMediaDuration })
    },
    getSmartSyncTag () {
      if (!hasPermissionToPublishToPlayer(this.$currentUser, this.isMediaAlertType)) {
        return
      }

      return this.userSchedules.find((option) => !option._edit?.disabled && !option.userDefined && option.scheduleId === 'smart-tags')
    },
    ledResolutionLabel () {
      let label = ''
      const res = this.$route.query.ledTags ?? this.$route.query.videowallTags

      if (res) {
        const resolution = this.$utils.parseLedTag(`LED:${res}`).name
        label = this.$t('label.withResolution', { resolution })
      }

      return label
    },
    getAlertActionButtonProp () {
      const hasScheduledMedia = !_.isEmpty(this.mediaPropertiesForm.values.schedules) && !_.isEmpty(this.mediaPropertiesForm.values.schedules[0])
      const isScheduleMediaEnabled = hasScheduledMedia && this.mediaPropertiesForm.values.schedules[0].enabled
      const isScheduleMenuTab = !this.formTabSelected || this.formTabSelected === this.formTabOptions.scheduleProperty
      const hastEditedProperty = !_.isEmpty(this.editableMediaProperties) || !_.isEmpty(this.editedMediaProperties)

      if (isScheduleMenuTab && hasScheduledMedia && isScheduleMediaEnabled) {
        // If the schedule tab and scheduled media are enabled, return the Deactivate button
        return {
          isVisible: true,
          onClick: () => this.handleActivateDeactivateAlertSchedules(false),
          label: this.$t('label.deactivate')
        }
      } else if (isScheduleMenuTab && hasScheduledMedia && !isScheduleMediaEnabled) {
        // If the schedule tab and scheduled media are NOT enabled, return the Activate button
        return {
          isVisible: true,
          onClick: () => this.handleActivateDeactivateAlertSchedules(true),
          label: this.$t('label.activate')
        }
      } else {
        // OK button by default and its visible if there's an edited property.
        return {
          isVisible: hastEditedProperty,
          onClick: () => this.handleMediaPropertiesFormSubmit(),
          label: this.$t('label.ok')
        }
      }
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('media-menu-change', { action, payload: { data } })
      }
    },
    /**
     * This will create and set property form values from selected media items
     * @param {Array} mediaSelectionSrc - Selected media
     */
    setMediaPropertiesFormFromMediaSelectionData (mediaSelectionSrc) {
      const mediaSelection = this.$utils.clone(mediaSelectionSrc)
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const isSingleSelection = this.mediaSelectionType === 'single'
      const isMultipleSelection = this.mediaSelectionType === 'multiple'
      const isMediaAlert = this.mediaSelection.findIndex((mediaItem) => { return mediaItem.alert }) >= 0

      // Combine all property values in array
      const name = []
      const combinedDuration = []
      const combinedSchedules = []
      const scheduleTargetsForEachMedia = [] // to compare if all schedule target are the same for each media
      const combinedTags = []
      const combinedValidity = []
      const combinedPopIds = new Set()

      for (const media of mediaSelection) {
        // Name
        name.push(media.name)

        // Duration
        if (media.dur) {
          combinedDuration.push(media.dur)
        }

        // Tags per media
        if (media.tags) {
          combinedTags.push({
            resourceId: media.resourceId,
            name: media.name,
            tags: media.tags
          })
        }

        // PopIds per media
        if (media.popId) {
          combinedPopIds.add(media.popId)
        }

        // Schedules
        if (media.schedules) {
          const scheduleTargets = []

          for (const schedule of media.schedules) {
            schedule._edit = { isSelected: false, mediaName: (media.name) ? media.name : 'no mediaName prop' }

            combinedSchedules.push(schedule)
            scheduleTargets.push(schedule.target)
          }
          scheduleTargetsForEachMedia.push(scheduleTargets.sort().join(','))
        }

        // Validity
        if (media.validity) {
          if (media.validity.start && media.validity.end) {
            combinedValidity.push(`${media.validity.start} ${media.validity.end}`)
          }
        }
      }

      // Set Name Property
      mediaPropertiesForm.values.name = name[0]

      // Set Media Duration to mm:ss
      if (isSingleSelection) {
        const dur = mediaSelection[0].dur ? mediaSelection[0].dur : this.defaultMediaDuration
        mediaPropertiesForm.values.duration = this.$utils.formatDurationToMinutesAndSeconds({ duration: dur })
        mediaPropertiesForm.values.popId = mediaSelection[0].popId ? mediaSelection[0].popId.trim() : null
      } else if (isMultipleSelection) {
        const allDurationAreTheSame = combinedDuration.length > 1 && combinedDuration.every((duration) => { return duration === combinedDuration[0] })
        let durationVal = 'clickToEdit'

        if (allDurationAreTheSame && combinedDuration.length) {
          durationVal = this.$utils.formatDurationToMinutesAndSeconds({ duration: combinedDuration[0] })
        }
        mediaPropertiesForm.values.duration = durationVal
      }

      // Set Tags
      let tagsValue = []
      if (combinedTags.length) {
        if (isSingleSelection) { // Single media selection
          tagsValue = combinedTags[0].tags
        } else if (isMultipleSelection) {
          const tagsSourceData = combinedTags[0].tags
          const hasTheSameTagsLength = combinedTags.every((mTags) => { return mTags.tags.length === tagsSourceData.length })
          const hasTheSameTagsValues = combinedTags.every((mTags) => { return mTags.tags.sort().join(',') === tagsSourceData.sort().join(',') })

          if (hasTheSameTagsLength && hasTheSameTagsValues) {
            for (const mediaTags of combinedTags) {
              tagsValue.push(...mediaTags.tags)
            }
          }
        }
      }

      mediaPropertiesForm.values.tags = this.$utils.removeDuplicateValuesFromArray(tagsValue)

      // Set Pop Id
      let popIdValue = null
      if (combinedPopIds.size) {
        const popIdIterator = combinedPopIds.values()

        if (isSingleSelection) {
          popIdValue = popIdIterator.next().value
        } else if (isMultipleSelection) {
          // check if all selected media have popIds
          const allSelectedMediaHavePopIds = mediaSelection.every(item => item.popId)

          // #1 if all media selected have popIds, find common popId value and show, else set popId to null
          if (allSelectedMediaHavePopIds) {
            // #2 if just one element in the Set, then it's common to all so show the value, otherwise set popId to null
            if (combinedPopIds.size === 1) {
              popIdValue = popIdIterator.next().value
            } else {
              popIdValue = null
            }
          } else {
            popIdValue = null
          }
        }
      }

      mediaPropertiesForm.values.popId = popIdValue

      // Set Validity
      mediaPropertiesForm.values.validityPresentation = 'NONE'
      mediaPropertiesForm.values.validity = []

      if (isSingleSelection) {
        if (combinedValidity.length === 1) {
          const validitySplt = combinedValidity[0].split(' ')

          mediaPropertiesForm.values.validity = [`${validitySplt[0]}`, `${validitySplt[1]}`]
          mediaPropertiesForm.values.validityPresentation = `${moment(validitySplt[0]).format('DD MMM YYYY')}
          - ${moment(validitySplt[1]).format('DD MMM YYYY')}`
        }
      } else if (isMultipleSelection) {
        const mostFrequentVal = this.$utils.getMostFrequentItemFromArray(combinedValidity)

        if (mostFrequentVal !== null && mostFrequentVal.length > 1) {
          const validitySplt = mostFrequentVal.split(' ')

          mediaPropertiesForm.values.validity = [`${validitySplt[0]}`, `${validitySplt[1]}`]
          mediaPropertiesForm.values.validityPresentation = `${moment(validitySplt[0]).format('DD MMM YYYY')}
          - ${moment(validitySplt[1]).format('DD MMM YYYY')}`
        }
      }

      mediaPropertiesForm.values.validityPresentation = this.formatValidityPresentation(mediaPropertiesForm.values.validity)

      // Set Schedules Property
      const officialCombinedSchedules = []
      const repeatedScheduleTargetSummary = {}

      for (const combinedSchedule of combinedSchedules) {
        // Count schedule in the list to check if it is repeated
        const countMatch = combinedSchedules.filter((sched) => combinedSchedule.target === sched.target).length

        // Convert old schedule playtime data
        if (combinedSchedule.type && combinedSchedule.type === 'daily') {
          combinedSchedule.type = 'custom'
          combinedSchedule.custom = [{
            uid: combinedSchedule.order,
            order: combinedSchedule.order,
            daily: true
          }]
        } else if (combinedSchedule.custom && combinedSchedule.custom.length === 1 && !combinedSchedule.custom[0].order) {
          combinedSchedule.custom[0].order = combinedSchedule.order
          combinedSchedule.custom[0].uid = combinedSchedule.order
        }

        // Add schedule target name
        combinedSchedule.name = this.getScheduleTargetDetails(combinedSchedule.target).name
        combinedSchedule.userDefined = this.getScheduleTargetDetails(combinedSchedule.target).userDefined
        combinedSchedule._edit = this.getScheduleTargetDetails(combinedSchedule.target)._edit

        // Make sure enable property is present for schedule alert
        if (combinedSchedule.type === 'alert' && !combinedSchedule.enabled) {
          combinedSchedule.enabled = false
        }

        // If schedule is repeated then create a summary group by target
        if (countMatch > 1) {
          if (!repeatedScheduleTargetSummary[combinedSchedule.target]) {
            repeatedScheduleTargetSummary[combinedSchedule.target] = []
          }
          repeatedScheduleTargetSummary[combinedSchedule.target].push(combinedSchedule)
        }

        // If schedule is not repeated then put this into the official list
        if (countMatch <= 1) {
          officialCombinedSchedules.push(combinedSchedule)
        }
      }

      // Set Schedule for alert if media alert
      mediaPropertiesForm.values.scheduleAlert = false
      if (isMediaAlert) {
        mediaPropertiesForm.values.scheduleAlert = true
      }

      // Process repeated schedule and finalize schedule list
      if (mediaPropertiesForm.values.scheduleAlert) { // Alert Schedule
        const isAllEnabledPropertyTheSameSummary = []
        for (const target in repeatedScheduleTargetSummary) {
          const newScheduleTargetValue = {
            target,
            name: this.getScheduleTargetDetails(target).name,
            userDefined: this.getScheduleTargetDetails(target).userDefined,
            type: 'alert',
            enabled: false,
            _edit: { isSelected: false, mediaName: 'combined media', backgroundColor: this.getScheduleTargetDetails(target)._edit.backgroundColor }
          }

          const combinedValues = []
          for (const indx in repeatedScheduleTargetSummary[target]) {
            const scheduleItem = repeatedScheduleTargetSummary[target][indx]
            combinedValues.push(scheduleItem.enabled)
          }

          const getCommonVal = this.$utils.getMostFrequentItemFromArray(combinedValues)
          newScheduleTargetValue.enabled = !!getCommonVal

          const isAllCombinedValuesTheSame = combinedValues.every((val) => { return (val === combinedValues[0]) })
          officialCombinedSchedules.push(newScheduleTargetValue)
          isAllEnabledPropertyTheSameSummary.push(isAllCombinedValuesTheSame)
        }

        // Finalize Schedule list
        let formScheduleList = officialCombinedSchedules.sort(this.$utils.arrayDynamicSort('name'))
        if (isMultipleSelection) {
          const isAllScheduleTargetForEachMediaTheSame = scheduleTargetsForEachMedia.every((scheduleTarget) => { return (scheduleTargetsForEachMedia[0] === scheduleTarget) })

          // If all schedule target are the same then show the list, else empty
          if (isAllScheduleTargetForEachMediaTheSame) {
            formScheduleList = officialCombinedSchedules

            // If all enabled are not the same then reset schedule list
            const isAllEnabledValTheSame = isAllEnabledPropertyTheSameSummary.every((val) => { return (isAllEnabledPropertyTheSameSummary[0] === val) })
            if (!isAllEnabledValTheSame) {
              formScheduleList = []
            }
          } else {
            formScheduleList = []
          }
        }

        mediaPropertiesForm.values.schedules = formScheduleList // Set schedule list
      } else { // Playtime Schedule
        const isAllCustomPlaytimeTheSameSummary = []
        for (const target in repeatedScheduleTargetSummary) {
          const newScheduleTargetValue = {
            target,
            name: this.getScheduleTargetDetails(target).name,
            userDefined: this.getScheduleTargetDetails(target).userDefined,
            type: 'custom',
            custom: [],
            _edit: { isSelected: false, mediaName: 'combined media', backgroundColor: this.getScheduleTargetDetails(target)._edit.backgroundColor }
          }

          const getCustomPlayTimeData = this.getCustomPlaytimeSummaryFromRepeatedSchedules(repeatedScheduleTargetSummary[target])
          newScheduleTargetValue.custom = getCustomPlayTimeData.customValue

          officialCombinedSchedules.push(newScheduleTargetValue)
          isAllCustomPlaytimeTheSameSummary.push(getCustomPlayTimeData.isAllCustomPlaytimeTheSame)
        }

        // Finalize Schedule list
        let formScheduleList = officialCombinedSchedules.sort(this.$utils.arrayDynamicSort('name'))
        if (isMultipleSelection) {
          const isAllScheduleTargetForEachMediaTheSame = scheduleTargetsForEachMedia.every((scheduleTarget) => { return (scheduleTargetsForEachMedia[0] === scheduleTarget) })

          // If all schedule target are the same then show the list, else empty
          if (isAllScheduleTargetForEachMediaTheSame) {
            formScheduleList = officialCombinedSchedules

            // If all custom playtime are not the same then reset schedule list
            const isAllCustomPlaytimeTheSame = isAllCustomPlaytimeTheSameSummary.every((val) => { return (isAllCustomPlaytimeTheSameSummary[0] === val) })
            if (!isAllCustomPlaytimeTheSame) {
              formScheduleList = []
            } else if (isAllCustomPlaytimeTheSame && isAllCustomPlaytimeTheSameSummary.length && !isAllCustomPlaytimeTheSameSummary[0]) {
              formScheduleList = []
            }
          } else {
            formScheduleList = []
          }
        }
        mediaPropertiesForm.values.schedules = formScheduleList // Set schedule list
      }

      // Normalize playtime data loaded from backend (strips empty date/time fields and tag)
      mediaPropertiesForm.values.schedules = mediaPropertiesForm.values.schedules.map((schedule) => {
        if (!schedule.custom) return schedule
        return { ...schedule, custom: normalizePlaytimes(schedule.custom) }
      })

      // Disabled smart-tags delete action for user tag rights
      if (this.$currentUser.getUserTags().length) {
        mediaPropertiesForm.values.schedules.forEach((schedule, indx) => {
          if (!mediaPropertiesForm.values.schedules[indx]._edit) mediaPropertiesForm.values.schedules[indx]._edit = {}
          mediaPropertiesForm.values.schedules[indx]._edit.disabledDelete = false

          if (schedule.target === 'smart-tags') {
            mediaPropertiesForm.values.schedules[indx]._edit.disabledDelete = true
          }
        })
      }

      // Set initial/update existing publishing option
      if (mediaPropertiesForm.values.schedules.length > 0) {
        const smartSyncSchedule = mediaPropertiesForm.values.schedules.find((sched) => sched.target === 'smart-tags')
        // If SMART SYNC is enabled, automatically show the play time schedule
        if (smartSyncSchedule) {
          this.publishOption = ['smartSync']
          mediaPropertiesForm.values.scheduleSelection = [smartSyncSchedule]
        } else {
          // Otherwise, reset Schedule List Selection
          this.publishOption = ['player']
          mediaPropertiesForm.values.scheduleSelection = []
        }
      } else {
        this.publishOption = []
        mediaPropertiesForm.values.scheduleSelection = []
      }

      // Reset scheduleNewTarget
      mediaPropertiesForm.values.scheduleNewTarget = {}

      // Let's create and set schedule target options for New Schedule Target and keep as not visible
      mediaPropertiesForm.values.scheduleNewTargetOptions = this.createNewScheduleTargetOptions(mediaPropertiesForm.values.schedules)

      // Hide smart-tags for user tag rights
      if (this.$currentUser.getUserTags().length) {
        mediaPropertiesForm.values.scheduleNewTargetOptions.forEach((schedule, indx) => {
          if (!mediaPropertiesForm.values.scheduleNewTargetOptions[indx]._edit) mediaPropertiesForm.values.scheduleNewTargetOptions[indx]._edit = {}
          mediaPropertiesForm.values.scheduleNewTargetOptions[indx]._edit.hideByTagUserRights = false

          if (schedule.scheduleId === 'smart-tags') {
            mediaPropertiesForm.values.scheduleNewTargetOptions[indx]._edit.hideByTagUserRights = true
          }
        })
      }

      // Set new schedule target plus icon visibility
      mediaPropertiesForm.values.scheduleNewTargetIconVisible = this.validateScheduleNewTargetIconVisibility(this.$currentUser, mediaPropertiesForm.values.scheduleNewTargetOptions, mediaPropertiesForm.values.schedules)

      this.mediaPropertiesForm = mediaPropertiesForm
    },
    handlePopIdVisibility () {
      this.showPopId = this.$currentUser.hasFeatureProofOfPlayExtended()
      const hasAudioFile = this.mediaSelection.find((media) => ['audio', 'audio-playlist'].includes(media.type))

      if (this.isMediaAlertType) {
        this.showPopId = false
      } else if (hasAudioFile) { // Hide for audio files
        this.showPopId = false
      }
    },
    filterUserSchedules (schedules) {
      let hasElementi = false
      let hasHTML = false
      let hasVideoIn = false

      if (this.mediaSelectionProp.find((media) => media.type === 'elementi')) {
        hasElementi = true
      }

      // Check type of medias inside projects
      const getMediaTypeProjects = this.mediaSelectionProp.filter((media) => media.type === 'project')
      for (const project of getMediaTypeProjects) {
        if (project.data && project.data.pages) {
          for (const page of project.data.pages) {
            for (const pageItemKey of Object.keys(page)) {
              if (pageItemKey.startsWith('media') || pageItemKey.startsWith('html') || pageItemKey.startsWith('videoin')) {
                switch (page[pageItemKey]?.type) {
                  case 'elementi':
                    hasElementi = true
                    break
                  case 'html':
                    hasHTML = true
                    break
                  case 'videoin':
                    hasVideoIn = true
                    break
                  default:
                    break
                }
              }
            }
          }
        }
      }

      const requiredFeatures = []
      if (hasElementi) {
        requiredFeatures.push('elementi')
      }
      if (hasHTML) {
        requiredFeatures.push('html')
      }
      if (hasVideoIn) {
        requiredFeatures.push('videoin')
      }

      const userSchedules = _.cloneDeep(schedules)

      const newUserSchedules = userSchedules.map((scheduleTarget, indx) => {
        if (!scheduleTarget._edit) {
          scheduleTarget._edit = {
            disabled: false,
            notSupported: false,
            backgroundColor: this.$utils.getBackgroundColor({ indx })
          }
        }

        scheduleTarget._edit.notSupported = false

        if (!scheduleTarget.userDefined) {
          scheduleTarget._edit.backgroundColor = '#d4d4d4'

          if (scheduleTarget.scheduleId === 'smart-tags') {
            scheduleTarget._edit.backgroundColor = this.$utils.getBackgroundColor({ indx: 1 })
          }

          if (scheduleTarget.scheduleId === 'all') {
            scheduleTarget._edit.backgroundColor = '#121212'
          }
        }

        if (requiredFeatures.length) {
          if (_.intersection(scheduleTarget.features ?? [], requiredFeatures).length === requiredFeatures.length) {
            scheduleTarget._edit.notSupported = false
          } else {
            scheduleTarget._edit.notSupported = true
          }
        } else {
          scheduleTarget._edit.notSupported = false
        }

        return scheduleTarget
      })

      return newUserSchedules
    },
    /**
     * This will get schedule name from user list of schedules
     * @param {String} scheduleTarget
     */
    getScheduleTargetDetails (scheduleTarget) {
      let schedTargetDetails = {
        userDefined: true,
        scheduleId: scheduleTarget,
        name: scheduleTarget,
        _edit: { backgroundColor: this.$utils.getBackgroundColor({ indx: Math.floor(Math.random() * 4) }), disabled: false }
      }

      const getDetails = this.userSchedules.find((userSchedule) => { return userSchedule.scheduleId === scheduleTarget })
      if (getDetails) {
        schedTargetDetails = getDetails
      }

      // Adjust name for tag-
      if (schedTargetDetails.name && schedTargetDetails.name.startsWith('tag-')) {
        schedTargetDetails.name = schedTargetDetails.name.substring(4)
      }

      return schedTargetDetails
    },
    getPlayerTagsColor (tagname, defaultColor) {
      let color = defaultColor

      // Get color from tags slider
      const detailsFromSlider = Array.isArray(this.tagsSummaryProp) && this.tagsSummaryProp.find((t) => { return t.name === tagname })
      if (detailsFromSlider && detailsFromSlider.color) {
        color = this.$utils.handleColorCodeConversion(detailsFromSlider.color, 'hex', 'rgba', 0.6)
      }

      return color
    },
    /**
     * This will combine schedule property values
     * @param {Array} schedules - list of schedules
     */
    getCustomPlaytimeSummaryFromRepeatedSchedules (collectionOfSchedulesEachMediaSrc) {
      const collectionOfSchedulesEachMedia = collectionOfSchedulesEachMediaSrc
      let newPlaytimeCustomValue = []
      let isAllCustomPlaytimeTheSame = true

      // Create custom playtime as source (for comparison to each playtime)
      let customPlaytimeSourceVal = []
      if (collectionOfSchedulesEachMedia[0].custom && collectionOfSchedulesEachMedia[0].custom.length > 0) {
        const customSource = collectionOfSchedulesEachMedia[0].custom
        if (customSource._edit) delete customSource._edit
        customPlaytimeSourceVal = customSource
      }

      // Check if all custom playtime has the same length
      const hasTheSameCustomLength = collectionOfSchedulesEachMedia.every((playtime) => {
        return (playtime.custom) && (playtime.custom.length === customPlaytimeSourceVal.length)
      })

      const findCustomPlaytimeFromSourceVal = (currentCustomVal) => {
        const getCustomValSummary = (customVal) => {
          if (customVal.daily) {
            return JSON.stringify({ daily: true })
          } else {
            return JSON.stringify({
              daily: (customVal.daily) ? customVal.daily : '',
              duration: (customVal.duration) ? customVal.duration : '',
              startDate: (customVal.startDate) ? customVal.startDate : '',
              endDate: (customVal.endDate) ? customVal.endDate : '',
              repeatDays: (customVal.repeatDays) ? customVal.repeatDays.sort() : ''
            })
          }
        }
        const currentCustomTimeVal = getCustomValSummary(currentCustomVal)

        // Find from source data
        let isFound = false
        for (const indx in customPlaytimeSourceVal) {
          const sourceCustomVal = getCustomValSummary(customPlaytimeSourceVal[indx])
          if (sourceCustomVal === currentCustomTimeVal) {
            isFound = true
          }
        }

        return isFound
      }

      // Compare each media schedule playtime value from created custom playtime source
      if (hasTheSameCustomLength) {
        let scheduleCustomPlaytimeSummary = []

        collectionOfSchedulesEachMedia.forEach((playtime, target) => {
          const playtimeCustom = (playtime.custom) ? playtime.custom : []

          // Compare each custom value from the source
          const customResults = []
          playtimeCustom.forEach((currentCustomVal) => {
            customResults.push(findCustomPlaytimeFromSourceVal(currentCustomVal))
          })
          scheduleCustomPlaytimeSummary = scheduleCustomPlaytimeSummary.concat(customResults)
        })

        const hasTheSameCustomPlaytime = scheduleCustomPlaytimeSummary.every((isTrue) => {
          return isTrue
        })

        if (hasTheSameCustomPlaytime) {
          newPlaytimeCustomValue = customPlaytimeSourceVal
        } else {
          isAllCustomPlaytimeTheSame = false
        }
      } else {
        isAllCustomPlaytimeTheSame = false
      }

      return {
        customValue: newPlaytimeCustomValue,
        isAllCustomPlaytimeTheSame
      }
    },
    /**
     * This will handle media selection
     * @param {Object} newSelection - new list of selected media
     */
    handleMediaSelection (newSelection) {
      let mediaSelectionType = null

      if (newSelection.length === 1) {
        mediaSelectionType = 'single'
      } else if (newSelection.length > 1) {
        mediaSelectionType = 'multiple'
      }

      this.mediaSelectionType = mediaSelectionType
      this.mediaSelection = newSelection
      this.formTabSelected = null

      // If this component access by mediaPage or schedulePage
      if (this.accessorComponent === 'mediaPage' || this.accessorComponent === 'ledPage' || this.accessorComponent === 'schedulePage') {
        this.editableMediaProperties = []
        this.editedMediaProperties = []
        this.mediaProperties = this.filterMediaProperties(this.mediaProperties, true)
        this.setMediaPropertiesFormFromMediaSelectionData(newSelection)
        this.hiddenMenuItems = []

        let hasProjectTypeInTheSelection = false
        for (const indx in this.mediaSelection) {
          if (this.mediaSelection[indx].type && this.mediaSelection[indx].type === 'project') {
            hasProjectTypeInTheSelection = true
            break
          }
        }

        const hasAudioAndAudioPlaylistTypeInTheSelection = this.mediaSelection.filter((item) => { return item.type && ['audio', 'audio-playlist'].indexOf(item.type) >= 0 }).length > 0

        // Duration field should not be visible if media selection has atleast one project type
        if (hasProjectTypeInTheSelection || hasAudioAndAudioPlaylistTypeInTheSelection) {
          this.hiddenMenuItems.push(this.mediaPropertiesId.duration)
        }

        // Tags filed should not be visible for Stand Alone user, Free User and Paid user but user create is lock
        if (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser()) {
          this.hiddenMenuItems.push(this.mediaPropertiesId.tags)
        }

        // PoP Identifier should not be visible for free user
        if (!this.$currentUser.hasFeatureProofOfPlayExtended() || (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser())) {
          this.hiddenMenuItems.push(this.mediaPropertiesId.popIdentifier)
        }

        // Form Tab Icon visibility
        if (this.$currentUser.hasGroupUserLimited()) {
          this.showCommonPropertiesTabIcon = false
          this.showSchedulePropertyTabIcon = false
        } else if (this.hiddenMenuItems.indexOf(this.mediaPropertiesId.duration) >= 0) {
          this.showCommonPropertiesTabIcon = false
          this.showSchedulePropertyTabIcon = false
        } else {
          this.showCommonPropertiesTabIcon = true
          this.showSchedulePropertyTabIcon = true
        }

        // Tags Menu Item Visibility
        this.isTagMenuItemVisible = this.$currentUser.getRights().userCreate.displayTags

        if (this.$currentUser.hasGroupUserLimited() || this.isMediaAlertType) {
          this.isTagMenuItemVisible = false
        }
      }

      if (this.accessorComponent === 'schedulePage') {
        this.hiddenMenuItems = [this.mediaPropertiesId.tags]
      }

      // If this component access by projectPage
      if (this.accessorComponent === 'projectPage') {
        this.editableMediaProperties = ['name', 'schedules']
        this.editedMediaProperties = []
        this.hiddenMenuItems = [this.mediaPropertiesId.tags]
        this.mediaProperties = this.filterMediaProperties(this.mediaProperties, false)
        this.setMediaPropertiesFormFromMediaSelectionData(newSelection)
        this.handlePropertyChange('schedules')

        // Change Edit Project Property Name
        const mediaProperties = this.mediaProperties
        mediaProperties.forEach((item, indx) => {
          if (item.id === 'editProject') {
            mediaProperties[indx].name = 'PREVIOUS'
          }
        })

        // Set Schedule in Media Form
        const mediaPropertiesForm = this.mediaPropertiesForm

        this.mediaPropertiesForm = mediaPropertiesForm
        this.mediaProperties = mediaProperties
      }
    },
    /**
     * This will handle property or menu item on click to add the item in the list of editable fields
     * @param {Object} propertyId - selected property Id
     */
    handlePropertyOnClick (propertyId) {
      const editableMediaProperties = this.editableMediaProperties

      if (editableMediaProperties.indexOf(propertyId) < 0) {
        editableMediaProperties.push(propertyId)
      }

      // Special case for Schedule Property
      if (propertyId === 'schedules') {
        const mediaPropertiesForm = this.mediaPropertiesForm
        this.mediaPropertiesForm = mediaPropertiesForm
      }

      if (propertyId === 'duration') {
        this.$nextTick(_ => {
          if (this.mediaPropertiesForm.values.duration === 'clickToEdit') {
            this.mediaPropertiesForm.values.duration = '00:10'
            this.handlePropertyChange('duration')
          }
          this.$refs.mediaDurationInput[0].$el.focus()
        })
      }

      this.editableMediaProperties = editableMediaProperties
      this.mediaProperties = this.filterMediaProperties(this.mediaProperties, false)
    },
    /**
     * This will handle property or menu item on change to add the item in the list of edited fields.
     * But if this component is used by projectPage, throw an action to inform that the media details is being updated
     * @param {String} propertyKey
     */
    handlePropertyChange (propertyKey) {
      const editedMediaProperties = this.editedMediaProperties

      if (editedMediaProperties.indexOf(propertyKey) < 0) {
        editedMediaProperties.push(propertyKey)
      }

      if (this.accessorComponent === 'projectPage') {
        const mediaItem = this.getMediaItemRequestData(this.mediaSelection[0], 0)

        // Add submit button type for media alert
        if (mediaItem.alert) {
          mediaItem.alertSubmitBtn = 'ok'

          if (mediaItem.schedules && mediaItem.schedules.length > 0) {
            mediaItem.alertSubmitBtn = 'activate'

            if (mediaItem.schedules[0].enabled) {
              mediaItem.alertSubmitBtn = 'reactivate'
            }
          }
        }

        this.emitEvent('UPDATE_PROJECT_TEMPLATE_MEDIA_DATA', mediaItem)
      }

      // Filter Duration value
      if (propertyKey === 'duration') {
        this.mediaPropertiesForm.values.duration = this.$utils.formatDurationToMinutesAndSeconds({
          duration: this.$utils.convertDurationToSeconds(this.mediaPropertiesForm.values.duration)
        })
      }

      this.editedMediaProperties = editedMediaProperties
    },
    /**
     * Handle Schedule List Item onClick. This will set the schedule item as selected or unselected
     * @param {Object} schedule
     */
    handleScheduleListItemClick ($event, schedule) {
      if (!this.enableScheduleChangeTarget) return

      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)

      if ($event.ctrlKey) {
        const findIndxFromSelection = mediaPropertiesForm.values.scheduleSelection.findIndex((sched) => { return sched.target === schedule.target })

        if (findIndxFromSelection >= 0) {
          mediaPropertiesForm.values.scheduleSelection.splice(findIndxFromSelection, 1)
        } else {
          mediaPropertiesForm.values.scheduleSelection.push(schedule)
        }
      } else {
        mediaPropertiesForm.values.scheduleSelection = [schedule]
      }

      this.mediaPropertiesForm = mediaPropertiesForm
      this.updateScheduleList({ newScheduleList: this.mediaPropertiesForm.values.schedules, scheduleSelection: mediaPropertiesForm.values.scheduleSelection })
    },
    /**
     * Handle delete schedule from schedule list
     * @param {Object} schedule
     */
    handleScheduleListItemDeleteClick (schedule) {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const newScheduleList = mediaPropertiesForm.values.schedules.filter((sched) => {
        return schedule.target !== sched.target
      })

      // When PUBLISH TO option is selected, and deleting the last player from the selection, reset publishing option to nothing
      if (this.publishOption.includes('player') && schedule.target !== 'smart-tags' && newScheduleList.length < 1) {
        this.publishOption = []
      }

      this.updateScheduleList({
        newScheduleList,
        scheduleSelection: [],
        targetSchedulePlaytime: []
      })

      // Delete schedule from each media selection
      if (this.mediaSelectionType === 'multiple') {
        const mediaSelection = this.$utils.clone(this.mediaSelection)

        mediaSelection.forEach((mediaItem) => {
          if (mediaItem.schedules) {
            mediaItem.schedules = mediaItem.schedules.filter((mediaItemSchedule) => {
              return (mediaItemSchedule.target !== schedule.target)
            })
          }
        })
        this.mediaSelection = mediaSelection
      }

      // Force to disabled all schedule so that user can click activate button for alert
      if (this.isMediaAlertType) {
        const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)

        for (const schedule of mediaPropertiesForm.values.schedules) {
          schedule.enabled = false
        }

        for (const schedule of mediaPropertiesForm.values.scheduleSelection) {
          schedule.enabled = false
        }

        this.mediaPropertiesForm = mediaPropertiesForm
      }

      // Set new schedule target plus icon visibility
      this.mediaPropertiesForm.values.scheduleNewTargetIconVisible = this.validateScheduleNewTargetIconVisibility(this.$currentUser, this.mediaPropertiesForm.values.scheduleNewTargetOptions, newScheduleList)

      // Add schedules to editable and edited list
      this.handlePropertyChange('schedules')
      this.handlePropertyOnClick('schedules')
    },
    showScheduleDropdownClick () {
      this.mediaPropertiesForm.values.scheduleNewTargetDropdownVisible = true

      // Create Schedule target Options
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      mediaPropertiesForm.values.scheduleNewTargetOptions = this.createNewScheduleTargetOptions(mediaPropertiesForm.values.schedules)
      this.mediaPropertiesForm = mediaPropertiesForm

      this.$nextTick(_ => {
        if (Array.isArray(this.$refs.newScheduleTargetDropdown) && this.$refs.newScheduleTargetDropdown.length) {
          this.$refs.newScheduleTargetDropdown[0].focus()
        }
      })
    },
    handleScheduleNewTargetFocus () {
      // Create Schedule target Options
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      mediaPropertiesForm.values.scheduleNewTargetOptions = this.createNewScheduleTargetOptions(mediaPropertiesForm.values.schedules)
      this.mediaPropertiesForm = mediaPropertiesForm
    },
    handleScheduleDropdownVisibleChange (visible) {
      if (visible) {
        const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
        mediaPropertiesForm.values.scheduleNewTargetOptions = this.createNewScheduleTargetOptions(mediaPropertiesForm.values.schedules)
        this.mediaPropertiesForm = mediaPropertiesForm
      } else {
        this.mediaPropertiesForm.values.scheduleNewTargetDropdownVisible = false
      }

      // Adjust warning icon position for unsupported players
      window.setTimeout(() => {
        $('.target-item-wrapper').each((index, targetEl) => {
          if ($(targetEl).hasClass('not-user-defined')) {
            const dropdownWrapperEl = $('.new-schedule-target-propper')
            const targetItemEl = $(targetEl).find('.target-item')
            const targetItemOffsetLeft = targetItemEl[0].offsetLeft
            const targetItemOffsetWidth = targetItemEl[0].offsetWidth
            let warninIconLeftOffset = targetItemOffsetLeft + targetItemOffsetWidth

            // Get max width of the dropdown when text is too long and used it as leftOffset.
            if (warninIconLeftOffset > dropdownWrapperEl.width()) {
              warninIconLeftOffset = dropdownWrapperEl.width() - 30
            }

            $(targetEl).find('.warning-icon').css({ left: `${warninIconLeftOffset}px` })
          }
        })
      }, 100)
    },
    /**
     * Receives selection value from PublishSelectionMenuItem and add object to
     * the mediaPropertiesForm for submission later
     * @param {object} value - The object selected from a list of scheduleNewTargetOptions
     */
    handlePlayerOptionSelection (value) {
      const selected = this.mediaPropertiesForm.values.scheduleNewTargetOptions.find((option) => option.scheduleId === value)

      this.mediaPropertiesForm.values.scheduleNewTarget = selected
      this.handleScheduleNewTargetSelectionChange()
    },
    /**
     * Handle Add New Schedule target Selectbox change
     * @param {Object} scheduleTarge - new schedule Target selected
     */
    handleScheduleNewTargetSelectionChange () {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const scheduleTarget = mediaPropertiesForm.values.scheduleNewTarget
      const newScheduleList = mediaPropertiesForm.values.schedules
      const createNewUID = this.$utils.generateUniqueID()
      const createNewOrder = this.$utils.calculateSortOrderValue()

      const smartSyncSchedIndex = newScheduleList.findIndex((sched) => sched.target === 'smart-tags')
      // Auto-select on initial selection of players or player tags
      if (scheduleTarget.scheduleId !== 'smart-tags' && !this.publishOption.includes('player')) {
        this.publishOption = ['player']
        this.handlePublishOption(['player'])
        newScheduleList.splice(smartSyncSchedIndex, 1)
      }

      const newScheduleItem = {
        name: scheduleTarget.name,
        target: scheduleTarget.scheduleId,
        type: 'custom',
        userDefined: scheduleTarget.userDefined,
        custom: [{ daily: true, uid: createNewUID, order: createNewOrder }],
        _edit: scheduleTarget._edit
      }

      if (newScheduleItem._edit.notSupported) {
        mediaPropertiesForm.values.scheduleNewTarget = {}
        this.mediaPropertiesForm = mediaPropertiesForm
        return
      }

      // Convert schedule target data for alert
      if (mediaPropertiesForm.values.scheduleAlert) {
        newScheduleItem.type = 'alert'
        newScheduleItem.enabled = false
        delete newScheduleItem.custom
      }

      newScheduleList.push(newScheduleItem) // Add new schedule to the current list

      // Force to disabled all schedule so that user can click activate button for alert
      if (this.isMediaAlertType) {
        for (const schedule of newScheduleList) {
          schedule.enabled = false
        }
      }

      // If there's current selection then get the current playtimes and merge this player to selection
      let scheduleSelection = mediaPropertiesForm.values.scheduleSelection
      if (scheduleSelection.length >= 1) {
        if (this.currentPlaytimes) {
          newScheduleItem.custom = normalizePlaytimes(this.$utils.clone(this.currentPlaytimes.custom))
        }
        scheduleSelection.push(newScheduleItem)
      } else {
        scheduleSelection = [newScheduleItem]
      }

      this.setScheduleData(newScheduleList, scheduleSelection)

      // Add schedules to editable and edited list
      this.handlePropertyChange('schedules')
      this.handlePropertyOnClick('schedules')

      // Reset new schedule target value
      this.mediaPropertiesForm.values.scheduleNewTargetDropdownVisible = false
      this.mediaPropertiesForm.values.scheduleNewTarget = {}
      this.mediaPropertiesForm.values.scheduleNewTargetIconVisible = this.validateScheduleNewTargetIconVisibility(this.$currentUser, this.mediaPropertiesForm.values.scheduleNewTargetOptions, mediaPropertiesForm.values.schedules)
    },
    /**
     * This will update Schedule List and some properties related to schedules in mediaPropertiesForm data
     * @param {Array} newListOfSchedules - list of schedules
     * @param {Array} scheduleSelection - list of selected schedules
     */
    updateScheduleList (params) {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const newScheduleList = params.newScheduleList
      const scheduleSelection = params.scheduleSelection
      const targetSchedulePlaytime = params.targetSchedulePlaytime ? params.targetSchedulePlaytime : []
      const playtimeItemsVisible = params.playtimeItemsVisible ? params.playtimeItemsVisible : 'all'

      // Set new schedule list
      mediaPropertiesForm.values.schedules = newScheduleList
      mediaPropertiesForm.values.schedules.forEach((schedule, indx) => {
        if (!schedule._edit) { schedule._edit = {} }

        // Set isSelected status based form schedule selection
        schedule._edit.isSelected = false
        const findFromScheduleSelection = scheduleSelection.find((selectedSchedule) => { return selectedSchedule.target === schedule.target })
        if (findFromScheduleSelection) {
          schedule._edit.isSelected = true
        }
      })

      if (targetSchedulePlaytime) {
        mediaPropertiesForm.values.schedulePlaytimeTarget = targetSchedulePlaytime
      }

      // Update Schedule Selection
      mediaPropertiesForm.values.scheduleSelection = scheduleSelection

      // Update New Schedule target options
      mediaPropertiesForm.values.scheduleNewTargetOptions = this.createNewScheduleTargetOptions(mediaPropertiesForm.values.schedules)

      // Set Playtime visible
      mediaPropertiesForm.values.playtimeItemsVisible = playtimeItemsVisible

      this.mediaPropertiesForm = mediaPropertiesForm
    },
    /**
     * Normalize playtime data on all schedules and update the form's schedule list.
     * All write paths that produce schedule/playtime data should call this instead
     * of updateScheduleList directly, so normalization happens in one place.
     * Callers are responsible for marking dirty state (handlePropertyChange / handlePropertyOnClick).
     * @param {Array} schedules
     * @param {Array} scheduleSelection
     * @param {Object} options
     * @param {Array}  options.targetSchedulePlaytime
     * @param {String} options.playtimeItemsVisible
     * @param {Object} options.newCurrentPlaytimes - if provided, updates this.currentPlaytimes
     */
    setScheduleData (schedules, scheduleSelection, options = {}) {
      const normalizeScheduleCustom = (list) => list.map((schedule) => {
        if (!schedule.custom) return schedule
        return { ...schedule, custom: normalizePlaytimes(schedule.custom) }
      })

      if (options.newCurrentPlaytimes !== undefined) {
        this.currentPlaytimes = options.newCurrentPlaytimes
      }

      this.updateScheduleList({
        newScheduleList: normalizeScheduleCustom(schedules),
        scheduleSelection: normalizeScheduleCustom(scheduleSelection),
        targetSchedulePlaytime: options.targetSchedulePlaytime ?? [],
        playtimeItemsVisible: options.playtimeItemsVisible ?? 'all',
      })
    },
    validateScheduleNewTargetIconVisibility (currentUser, currentScheduleOptions, currentSchedules) {
      if (!hasPermissionToPublishToPlayer(currentUser, this.isMediaAlertType)) {
        return false
      }

      let hasSpecialTargetSchedule = false

      if (this.isMediaAlertType) {
        hasSpecialTargetSchedule = currentSchedules.find((schedule) => { return schedule.target === 'all' })
      }

      if (currentScheduleOptions.length && hasSpecialTargetSchedule) {
        return false
      }

      return true
    },
    /**
     * Create new schedule target options for dropdown
     * @param {Array} - currentSchedules
     */
    createNewScheduleTargetOptions (currentSchedules) {
      let scheduleNewTargetOptions = []

      if (currentSchedules.length > 0) {
        // Original User Schedules Target
        for (const scheduleTarget of this.userSchedules) {
          const isFound = currentSchedules.find((sched) => { return sched.target === scheduleTarget.scheduleId })
          if (!isFound) scheduleNewTargetOptions.push(scheduleTarget)
        }
      } else {
        scheduleNewTargetOptions = this.userSchedules
      }

      // Disabled Smart Tags and All if not selected
      scheduleNewTargetOptions = scheduleNewTargetOptions.filter((schedule) => {
        schedule._edit.disabled = false

        if ((this.isMediaAlertType && schedule.scheduleId === 'all') && (currentSchedules.length && !currentSchedules.find((schedule) => { return schedule.target === 'all' }))) {
          schedule._edit.disabled = true
        }

        return schedule
      })

      return scheduleNewTargetOptions
    },
    /**
     * This will handle next actions after an API call in each media selection
     * @param {String} typeOfResponse
     * @param {Object} responseData
     * @param {String} resourceId
     */
    handleNextActionsAfterAPIRequestInEachMediaItem (requestType, typeOfResponse, responseData, resourceId, params) {
      const mediaSelectionAPIRequestFlag = this.$utils.clone(this.mediaSelectionAPIRequestFlag)
      let countRequest = 0

      // Update media data in API request flag. Also count all media request status to determine if there existing request
      for (const media of mediaSelectionAPIRequestFlag) {
        if (media.resourceId === resourceId) {
          media.apiRequestStatus = 'done'
          media.response = { type: typeOfResponse, responseData }
        }

        if (media.apiRequestStatus === 'requesting') {
          countRequest += 1
        }
      }
      this.mediaSelectionAPIRequestFlag = mediaSelectionAPIRequestFlag

      /**
       * All API request are done so:
       * - Update media selection data
       * - Process all media with error request
       * - Reset edited media properties flag
       * - Close the loader
       */
      if (countRequest === 0) {
        this.editedMediaProperties = []
        this.$utils.mainLoader(false)
        this.removeUnsavedDataFlagInLocalStorage()

        this.emitEvent('UPDATE_MEDIA_DATA', {
          requestType,
          updatedMedia: mediaSelectionAPIRequestFlag,
          mediaSelectionBeforRequest: this.mediaSelectionAPIRequestFlag,
          triggerPropertyAction: this.triggerPropertyAction,
          ...params
        })
      }
    },
    /**
     * This will create media item request data
     * @param {Object} mediaItem - original media item data
     * @param {Number} mediaItemPosition - position from the list
    */
    getMediaItemRequestData (mediaItem, mediaItemPosition) {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
      const isMultipleMediaSelection = this.mediaSelectionType === 'multiple'
      const isSingleMediaSelection = this.mediaSelectionType === 'single'
      const editableMediaProperties = this.editableMediaProperties
      const editedMediaProperties = this.editedMediaProperties

      const requestData = {
        name: mediaItem.name,
        tags: mediaItem.tags ? mediaItem.tags : [],
        validity: mediaItem.validity ? mediaItem.validity : {},
        type: mediaItem.type ? mediaItem.type : '',
        popId: mediaItem.popId ? mediaItem.popId : null
      }
      if (mediaItem.data) requestData.data = mediaItem.data
      if (mediaItem.alert) requestData.alert = mediaItem.alert

      // Set schedules if user has rights to edit schedules
      if (this.$currentUser.hasGroupUserAdmin() || this.$currentUser.hasGroupUserSchedule()) {
        requestData.schedules = mediaItem.schedules ? mediaItem.schedules : []
      }

      // Set Media Duration
      if (mediaItem.type !== 'project' && mediaPropertiesForm.values.duration !== null && mediaPropertiesForm.values.duration !== 'clickToEdit') {
        requestData.dur = this.$utils.convertDurationToSeconds(mediaPropertiesForm.values.duration)
      }

      // Set schedules
      if (editedMediaProperties.indexOf('schedules') >= 0) {
        const mediaSchedulesFromForm = this.$utils.clone(mediaPropertiesForm.values.schedules)

        // Cleanup Properties — strip UI-only fields before sending to the API
        for (const schedule of mediaSchedulesFromForm) {
          delete schedule._edit

          if (schedule.custom && schedule.custom.length > 0) {
            for (const playtimePosition in schedule.custom) {
              const customPlaytime = schedule.custom[playtimePosition]
              // UI-related state is not needed in the API request
              if (customPlaytime._edit) {
                delete customPlaytime._edit
              }
            }
          }
        }

        // Update uid and order for each playtime to avoid duplicates
        mediaSchedulesFromForm.forEach((schedule, scheduleIndex) => {
          if (schedule.custom) {
            for (const playtimeIndex in schedule.custom) {
              const playtime = schedule.custom[playtimeIndex]
              const newUidVal = `${this.$utils.generateUniqueID()}-${playtimeIndex}`
              const newOrderVal = this.$utils.calculateSortOrderValue({ mediaIndex: mediaItemPosition, scheduleIndex, playtimeIndex })

              // For multiple media selecton, update uid and order
              if (isMultipleMediaSelection) {
                playtime.uid = newUidVal
                playtime.order = newOrderVal
              } else if (isSingleMediaSelection) { // For single media selection, update only those playtime with NO uid and order
                if (this.accessorComponent === 'projectPage' || !playtime.uid) {
                  playtime.uid = newUidVal
                }

                if (!playtime.order || playtime.order === null) {
                  playtime.order = newOrderVal
                }
              }
            }
          }
        })

        requestData.schedules = mediaSchedulesFromForm
      }

      // Set tags
      if (editedMediaProperties.indexOf('tags') >= 0 || editableMediaProperties.indexOf('tags') >= 0) {
        requestData.tags = mediaPropertiesForm.values.tags
      }

      // If popId media property was edited OR if it's included in the set editable properties based on user's rights, set PoP Identifier
      if (editedMediaProperties.indexOf('popIdentifier') >= 0 || editableMediaProperties.indexOf('popIdentifier') >= 0) {
        requestData.popId = mediaPropertiesForm.values.popId
      }

      // Set validity
      if ((editedMediaProperties.indexOf('validity') >= 0) &&
        (mediaPropertiesForm.values.validity !== null && mediaPropertiesForm.values.validity.length > 0)) {
        requestData.validity = {
          start: mediaPropertiesForm.values.validity[0],
          end: mediaPropertiesForm.values.validity[1]
        }
      }

      // Remove duration if media has a status flag
      if (mediaItem.status && Object.prototype.hasOwnProperty.call(requestData, 'dur')) {
        delete requestData.dur
      }

      return requestData
    },
    handleMediaPropertiesFormSubmit (params = {}) {
      const mediaSelection = this.$utils.clone(this.mediaSelection)
      const isInputNewTagActive = $('.custom-tags-auto-complete-dropdown').length
      const playtimeFormActive = $('#playtimeForm').length
      const saveMediaData = () => {
        const mediaSelectionAPIRequestFlag = [] // Media selection API requests flag. By this, we can track each media request results
        mediaSelection.forEach((mediaItem, indx) => {
          mediaSelectionAPIRequestFlag.push({
            resourceId: mediaItem.resourceId,
            apiRequestStatus: 'requesting',
            response: { type: null, responseData: {} },
            origDetails: mediaItem
          })
        })
        this.mediaSelectionAPIRequestFlag = mediaSelectionAPIRequestFlag

        /**
         * Note: If media has status(uploading) save the data in localStorage instead of sending to backend. The fileUploader component will
         * get that data and send to backend once conversion file is finished
         */
        if (mediaSelectionAPIRequestFlag.length) this.$utils.mainLoader(true)
        mediaSelection.forEach((mediaItem, indx) => {
          const requestData = this.getMediaItemRequestData(mediaItem, indx)
          const pageOrientation = this.$dataStore.getMediaOrientation()
          const width = 424
          const sendToBackendRequest = () => {
            this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?use_qsa=1&dir=${pageOrientation}&width=${width}`, requestData)
              .then(response => {
                this.handleNextActionsAfterAPIRequestInEachMediaItem('put', 'success', response.data, mediaItem.resourceId, params)
              })
              .catch((error) => {
                this.handleNextActionsAfterAPIRequestInEachMediaItem('put', 'error', error, mediaItem.resourceId, params)
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { sendToBackendRequest() } })
              })
          }
          const saveToLocalStorage = () => {
            const targetComponents = [
              TARGET_COMPONENTS.FILES_PAGE,
              TARGET_COMPONENTS.ALERT_PAGE,
              TARGET_COMPONENTS.LED_PAGE,
              TARGET_COMPONENTS.VIDEOWALL_PAGE
            ]
            const filesInLocalStorage = this.fileUploadStorage.getFiles()
            const getIndex = filesInLocalStorage.findIndex((file) => {
              return (file._edit && file._edit.resourceId === mediaItem.resourceId) &&
              (targetComponents.indexOf(file._edit.targetComponent) >= 0)
            })

            if (getIndex >= 0) {
              filesInLocalStorage[getIndex]._edit.updateMediaDataOnceConversionFinished.updateMediaRequestData = this.$utils.clone(requestData)
              this.fileUploadStorage.upsertFile(filesInLocalStorage[getIndex])
              const asUpdatedMedia = { ...this.$utils.clone(mediaItem), ...requestData }
              this.handleNextActionsAfterAPIRequestInEachMediaItem('put', 'success', asUpdatedMedia, mediaItem.resourceId, params)
            }
          }

          if (!mediaItem.status) {
            sendToBackendRequest()
          } else {
            saveToLocalStorage()
          }
        })
      }

      // If tag input is active, added a timeout to receive the updated data from menu-tag-item component
      if (isInputNewTagActive || playtimeFormActive) {
        window.setTimeout(() => saveMediaData(), 10)
      } else {
        saveMediaData()
      }
    },
    /**
     *
     * @param {Object} mediaPropertiesSrc - media properties to update
     * @param {Boolean} resetEditableStatus - reset each editable status
     */
    filterMediaProperties (mediaPropertiesSrc, resetEditableStatus) {
      const mediaProperties = this.$utils.clone(mediaPropertiesSrc)
      const editableMediaProperties = this.editableMediaProperties

      for (const i in mediaProperties) {
        const mediaProp = mediaProperties[i]

        mediaProp.editable = false
        if ((editableMediaProperties.indexOf(mediaProp.id) >= 0) && (!resetEditableStatus)) {
          mediaProp.editable = true
        }
      }
      return mediaProperties
    },
    handleMediaPropertyDeleteClick () {
      const mediaSelection = this.$utils.clone(this.mediaSelection)
      const uploadedFilesInLocalStorage = this.fileUploadStorage.getFiles().filter((file) => { return file !== null })
      const mediaDeletedThatAreInUploadingState = []

      // Check if there are media that are in uploading state
      if (uploadedFilesInLocalStorage.length) {
        mediaSelection.forEach((mediaItem) => {
          const mediaInUploadListIndx = uploadedFilesInLocalStorage.findIndex((file) => { return (file && file._edit) && file._edit.resourceId === mediaItem.resourceId })

          if (mediaInUploadListIndx >= 0) {
            mediaDeletedThatAreInUploadingState.push(mediaItem)
            uploadedFilesInLocalStorage.splice(mediaInUploadListIndx, 1)
          }
        })
      }

      const deleteCount = mediaSelection.length
      const isMultipleDelete = deleteCount > 1
      const description = isMultipleDelete
        ? {
            key: 'media.multipleMediaDeleteConfirmation',
            params: { mediaCount: deleteCount }
          }
        : {
            key: 'media.mediaDeleteConfirmation', params: { mediaName: mediaSelection[0].name }
          }
      const alert = {
        message: isMultipleDelete ? this.$t('media.multipleMediaDeleteWarning') : this.$t('media.mediaDeleteWarning')
      }
      // Send global event to show custom delete confirmation dialog
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: this.$t('confirm.deleteMedia'),
        descriptionI18n: description,
        alert,
        confirmButtonText: this.$t('buttons.deleteMedia'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOnClose: () => {
            this.triggerPropertyAction = {}
            this.emitEvent('RESET_MEDIA_MENU_TEMPLATE_VISIBILITY', {})
          },
          eventOKButtonClick: () => {
            // Create media selection API Request process flag. By this, we can track each media request results
            const mediaSelectionAPIRequestFlag = []
            for (const mediaItem of mediaSelection) {
              mediaSelectionAPIRequestFlag.push({
                resourceId: mediaItem.resourceId,
                apiRequestStatus: 'requesting',
                response: { type: null, responseData: {} },
                origDetails: mediaItem
              })
            }
            this.mediaSelectionAPIRequestFlag = mediaSelectionAPIRequestFlag

            // Notify the backend that the upload was interupted
            if (mediaDeletedThatAreInUploadingState.length) {
              mediaDeletedThatAreInUploadingState.forEach((mediaItem) => {
                const requestData = { status: 'cancel', reason: 'Deleted by the user while in uploading state' }
                this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}`, requestData)
                this.fileUploaderStore.notifyFileDeleted(mediaItem)
              })

              this.fileUploadStorage.setFiles(uploadedFilesInLocalStorage)
            }

            // Send each selected media into the backend
            this.$utils.mainLoader(true)
            for (const mediaItem of mediaSelection) {
              const apiRequest = () => {
                let deleteUrl = `${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?checkinuse=1`
                if (this.isMediaAlertType) deleteUrl += '&alert=1'

                this.$api.delete(deleteUrl)
                  .then(response => {
                    this.handleNextActionsAfterAPIRequestInEachMediaItem('delete', 'success', response.data, mediaItem.resourceId)
                  })
                  .catch((error) => {
                    this.handleNextActionsAfterAPIRequestInEachMediaItem('delete', 'error', error, mediaItem.resourceId)
                    this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
                  })
              }
              apiRequest()
            }
          }
        }
      })
    },
    handleForceDeleteMedia (mediaItems) {
      // Let's create media selection API Request process flag. By this, we can track each media request results
      const mediaSelectionAPIRequestFlag = []
      for (const mediaItem of mediaItems) {
        mediaSelectionAPIRequestFlag.push({
          resourceId: mediaItem.resourceId,
          apiRequestStatus: 'requesting',
          response: { type: null, responseData: {} },
          origDetails: mediaItem
        })
      }
      this.mediaSelectionAPIRequestFlag = mediaSelectionAPIRequestFlag

      // Let's send each selected media into the API
      this.$utils.mainLoader(true)
      for (const mediaItem of mediaItems) {
        const apiRequest = () => {
          let deleteUrl = `${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}`
          if (this.isMediaAlertType) deleteUrl += '?alert=1'

          this.$api.delete(deleteUrl)
            .then(response => {
              this.handleNextActionsAfterAPIRequestInEachMediaItem('delete', 'success', response.data, mediaItem.resourceId)
            })
            .catch((error) => {
              this.handleNextActionsAfterAPIRequestInEachMediaItem('delete', 'error', error, mediaItem.resourceId)
              this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
            })
        }

        apiRequest()
      }
    },
    handleProjectEditButtonClick () {
      if (this.accessorComponent === 'mediaPage') {
        this.$router.push(`/project-edit/${this.mediaSelection[0].resourceId}/edit-frame`, () => {})
      } else if (this.accessorComponent === 'projectPage') {
        this.emitEvent('EDIT_PROJECT_TEMPLATE', {})
      }
    },
    /**
     * Handle direct call to trigger property action from different component
     */
    handleTriggerPropertyAction () {
      const triggerPropertyActionVal = this.$utils.clone(this.triggerPropertyAction)
      const updatePlaytimeListInForm = (actionType, scheduleId, uid, order, newValues) => {
        const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)
        const scheduleIndx = mediaPropertiesForm.values.schedules.findIndex((schedule) => { return schedule.target === scheduleId })
        const targetSchedule = mediaPropertiesForm.values.schedules[scheduleIndx]

        let playtimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === uid })
        if (playtimeIndx < 0) playtimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.order === order })

        // Playtime found in schedule lists
        if (playtimeIndx >= 0) {
          if (actionType === 'updatePlaytimeOrder') {
            targetSchedule.custom[playtimeIndx].order = newValues.newOrder
          }

          if (actionType === 'deletePlaytimeRow') {
            targetSchedule.custom.splice(playtimeIndx, 1)
          }
        }

        // Update playtime in selected schedule
        const scheduleIndxInSelectedSchedule = mediaPropertiesForm.values.scheduleSelection.findIndex((schedule) => { return schedule.target === scheduleId })
        const targetScheduleSel = mediaPropertiesForm.values.scheduleSelection[scheduleIndxInSelectedSchedule]
        let playtimeIndxSel = targetScheduleSel.custom.findIndex((playtime) => { return playtime.uid === uid })
        if (playtimeIndxSel < 0) playtimeIndxSel = targetScheduleSel.custom.findIndex((playtime) => { return playtime.order === order })

        // Playtime found in selected schedule
        if (playtimeIndxSel >= 0) {
          if (actionType === 'updatePlaytimeOrder') {
            targetScheduleSel.custom[playtimeIndxSel].order = newValues.newOrder
          }

          if (actionType === 'deletePlaytimeRow') {
            targetScheduleSel.custom.splice(playtimeIndxSel, 1)
          }
        }

        return {
          updatedScheduleList: mediaPropertiesForm.values.schedules,
          updatedTargetSchedule: targetScheduleSel,
          updatedTargetPlaytime: targetScheduleSel.custom[playtimeIndxSel]
        }
      }

      // Edit Schedule action
      if (triggerPropertyActionVal.action === 'editMediaSchedule') {
        const newMediaSelection = this.$utils.clone(triggerPropertyActionVal.mediaSelection)

        this.handleMediaSelection(newMediaSelection)
        const findTargetScheduleIndx = this.mediaPropertiesForm.values.schedules.findIndex((schedule) => { return schedule.target === triggerPropertyActionVal.scheduleId })
        const targetSchedule = this.mediaPropertiesForm.values.schedules[findTargetScheduleIndx]
        const targetSchedulePlaytime = (triggerPropertyActionVal.selectedPlaytime) ? triggerPropertyActionVal.selectedPlaytime : []
        const playtimeItemsVisible = (triggerPropertyActionVal.selectedPlaytime.length) ? triggerPropertyActionVal.playtimeItemsVisible : 'all'

        // Update schedule list selection
        this.updateScheduleList({
          newScheduleList: this.mediaPropertiesForm.values.schedules,
          scheduleSelection: [targetSchedule],
          targetSchedulePlaytime,
          playtimeItemsVisible
        })

        if (triggerPropertyActionVal.showMediaMenuSubmitButton) {
          this.handlePropertyChange(this.mediaPropertiesId.schedules)
          this.handlePropertyOnClick(this.mediaPropertiesId.schedules)
        }
      }

      // Submit media menu form action
      if (['autoSavedPlaytime', 'submitMediaForm'].indexOf(triggerPropertyActionVal.action) >= 0) {
        this.handleMediaPropertiesFormSubmit()
      }

      // Update Schedule playtime data action
      if (triggerPropertyActionVal.action === 'playtimeOrderUpdated') {
        const updatedSchedulePlaytime = updatePlaytimeListInForm(
          'updatePlaytimeOrder',
          triggerPropertyActionVal.scheduleId,
          triggerPropertyActionVal.uid,
          triggerPropertyActionVal.previousOrder,
          { newOrder: triggerPropertyActionVal.newOrder })
        const targetSchedulePlaytime = [{ uid: triggerPropertyActionVal.uid, order: triggerPropertyActionVal.newOrder }]

        // Update schedule list selection
        this.updateScheduleList({
          newScheduleList: updatedSchedulePlaytime.updatedScheduleList,
          scheduleSelection: [updatedSchedulePlaytime.updatedTargetSchedule],
          targetSchedulePlaytime,
          playtimeItemsVisible: triggerPropertyActionVal.playtimeItemsVisible
        })
      }

      // Delete Schedule playtime data action
      if (triggerPropertyActionVal.action === 'deletePlaytimeRow') {
        const updatedSchedulePlaytime = updatePlaytimeListInForm(
          'deletePlaytimeRow',
          triggerPropertyActionVal.playtimeDeleted.scheduleId,
          triggerPropertyActionVal.playtimeDeleted.uid,
          triggerPropertyActionVal.playtimeDeleted.order)

        // Update schedule list selection
        this.updateScheduleList({
          newScheduleList: updatedSchedulePlaytime.updatedScheduleList,
          scheduleSelection: [updatedSchedulePlaytime.updatedTargetSchedule],
          targetSchedulePlaytime: [],
          playtimeItemsVisible: triggerPropertyActionVal.playtimeItemsVisible
        })
      }

      // Delete schedule action
      if (triggerPropertyActionVal.action === 'deleteMediaSchedule') {
        this.handleMediaSelection(triggerPropertyActionVal.mediaSelection)
        this.handleScheduleListItemDeleteClick({ target: triggerPropertyActionVal.scheduleId })
        this.handleMediaPropertiesFormSubmit()
      }

      // Delete media action
      if (triggerPropertyActionVal.action && triggerPropertyActionVal.action === 'deleteMediaItem') {
        if (this.accessorComponent === 'mediaPage' || this.accessorComponent === 'ledPage') {
          this.handleMediaPropertyDeleteClick()
        }
      }

      if (triggerPropertyActionVal.action && triggerPropertyActionVal.action === 'mediaAttributeValueChanged') {
        this.handlePropertyChange(triggerPropertyActionVal.attributeName)
      }

      // Reset media selection action
      if (triggerPropertyActionVal.action === 'resetMediaSelection') {
        this.handleMediaSelection([])
      }
    },
    handleSubComponentChange (event, subComponentName) {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)

      // Tags
      if (subComponentName === 'tagMenuItem' && (event.action === 'TAGS_UPDATED' || event.action === 'TAG_REMOVED')) {
        mediaPropertiesForm.values.tags = event.payload.data.tags
        this.mediaPropertiesForm = mediaPropertiesForm
        this.handlePropertyOnClick(this.mediaPropertiesId.tags)
        this.handlePropertyChange(this.mediaPropertiesId.tags)
      }

      if (subComponentName === 'schedulePlaytimeMenuItem' && event.action === 'PLAYTIMES_BEFORE_UPDATE') {
        this.currentPlaytimes = {
          type: 'custom',
          custom: event.payload.data.playtimes
        }
      }

      // PoP Identifier
      if (subComponentName === 'popIdentifierMenuItem' && event.action === 'POP_IDENTIFIER_UPDATED') {
        const allowed = this.verifyPopIdAssignability(event.payload.data.prevValue, event.payload.data.newValue)

        if (allowed) {
          mediaPropertiesForm.values.popId = event.payload.data.newValue
          this.mediaPropertiesForm = mediaPropertiesForm
          this.handlePropertyChange(this.mediaPropertiesId.popIdentifier)
          this.handlePropertyOnClick(this.mediaPropertiesId.popIdentifier)
        } else {
          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
            type: 'error',
            alert: {
              message: this.$t('label.multiplePopIdsNotAllowed')
            },
            callbacks: {
              eventOnClose: () => {
                this.emitEvent('RESET_MEDIA_MENU_TEMPLATE_VISIBILITY', {})
              }
            }
          })
        }
      }

      // Schedule Play Time
      if (subComponentName === 'schedulePlaytimeMenuItem' && event.action === 'PLAYTIMES_UPDATED') {
        const targetScheduleForNewPlaytimes = event.payload.data.scheduleTarget
        const prevPlaytimeActionType = event.payload.data.actionType
        const playtimes = event.payload.data.playtimes

        const newScheduleData = { type: 'custom', custom: playtimes }

        // Replace playtimes on matching schedule targets in both lists
        const updatedSchedules = this.$utils.clone(this.mediaPropertiesForm.values.schedules).map((schedule) => {
          if (targetScheduleForNewPlaytimes.indexOf(schedule.target) >= 0) {
            return { ...schedule, ...newScheduleData }
          }
          return schedule
        })

        const updatedSelection = this.$utils.clone(this.mediaPropertiesForm.values.scheduleSelection).map((schedule) => {
          if (targetScheduleForNewPlaytimes.indexOf(schedule.target) >= 0) {
            return { ...schedule, ...newScheduleData }
          }
          return schedule
        })

        this.setScheduleData(updatedSchedules, updatedSelection, {
          newCurrentPlaytimes: newScheduleData,
        })
        this.handlePropertyChange(this.mediaPropertiesId.schedules)
        this.handlePropertyOnClick(this.mediaPropertiesId.schedules)

        // If this component used by schedule page, inform the page that new playtime was created to update the events list
        if (this.accessorComponent === 'schedulePage' && prevPlaytimeActionType === 'createNewPlayTime') {
          this.triggerPropertyAction = {
            action: 'newCreatedPlaytimeFromMediaMenu',
            newCreatedPlaytime: {
              playtimeTarget: {
                uid: event.payload.data.playtimes[event.payload.data.playtimes.length - 1].uid,
                order: event.payload.data.playtimes[event.payload.data.playtimes.length - 1].order
              },
              mediaSelection: this.mediaSelection
            }
          }
          this.handleMediaPropertiesFormSubmit()
        }
      }
    },
    handleActivateDeactivateAlertSchedules (isActivate) {
      const mediaPropertiesForm = this.$utils.clone(this.mediaPropertiesForm)

      // Update the enabled property for schedules
      mediaPropertiesForm.values.schedules = mediaPropertiesForm.values.schedules.map(schedule => ({
        ...schedule,
        enabled: isActivate
      }))

      // Update the enabled property for scheduleSelection
      mediaPropertiesForm.values.scheduleSelection = mediaPropertiesForm.values.scheduleSelection.map(schedule => ({
        ...schedule,
        enabled: isActivate
      }))

      this.mediaPropertiesForm = mediaPropertiesForm
      this.handlePropertyChange(this.mediaPropertiesId.schedules)
      this.handleMediaPropertiesFormSubmit()
    },
    setPopIdSummary () {
      if (this.accessorComponent === 'projectPage' && this.showPopId) {
        const getPopIdsRequest = () => {
          this.$api.get(`${CONFIG.API_BASE_URL}/media/popid`)
            .then(response => {
              this.popIdsSummary = response.data.popIds
            })
            .catch((error) => {
              this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getPopIdsRequest() }, showDialogNotification: false })
            })
        }
        getPopIdsRequest()
      } else {
        this.popIdsSummary = this.popIdsSummaryProp
      }
    },
    verifyPopIdAssignability (prevPopIdValue, newPopIdValue) {
      let allowed = false

      // Always allow for single selection
      // OR when just deleting the currently assigned common popId
      if (this.mediaSelection.length <= 1 || !newPopIdValue) {
        return true
      }

      if (prevPopIdValue !== null) {
        // When just renaming the currently assigned common popId
        allowed = true
      } else {
        // Allow assignment to a media with no popId ONLY IF it's a common popId value
        allowed = this.mediaSelection.every((item) => item.popId ? item.popId === newPopIdValue : true)
      }

      return allowed
    },
    handlePublishOption (value) {
      // Remove the first option if there is more than one option
      if (this.publishOption.length > 1) {
        this.publishOption.shift()
      }

      // If something has been selected
      if (value.length > 0) {
        // If SMART SYNC has been selected, remove all previously selected players/player groups
        if (value[0] === 'smartSync') {
          const allSchedules = this.mediaPropertiesForm.values.schedules
          if (allSchedules.length > 0) {
            allSchedules.forEach(sched => {
              this.handleScheduleListItemDeleteClick(sched)
            })
          }

          const smartSyncOption = this.mediaPropertiesForm.values.scheduleNewTargetOptions.find((option) => option.scheduleId === 'smart-tags')

          if (smartSyncOption) {
            this.mediaPropertiesForm.values.scheduleNewTarget = smartSyncOption
            this.handleScheduleNewTargetSelectionChange()
          }
        } else {
          // If PUBLISH TO option selected, remove smart sync selection
          const smartSyncSched = this.mediaPropertiesForm.values.schedules.find((sched) => sched.target === 'smart-tags')

          if (smartSyncSched) {
            this.handleScheduleListItemDeleteClick(smartSyncSched)
          }
        }
      } else { // If de-selecting, remove all schedules
        const allSchedules = this.mediaPropertiesForm.values.schedules
        if (allSchedules.length > 0) {
          allSchedules.forEach(sched => {
            this.handleScheduleListItemDeleteClick(sched)
          })
        }
      }
    },
    handleSmartSyncHelpClick () {
      window.open(this.smartSyncHelpLink)
    },
    handleDurationchanged (value) {
      this.mediaPropertiesForm.values.duration = value
      this.handlePropertyChange('duration')
    }
  },
  beforeUnmount () {
    this.removeUnsavedDataFlagInLocalStorage()
  },
  mounted () {
    this.userSchedules = this.filterUserSchedules(this.userSchedulesProp)
    this.handlePopIdVisibility()
    this.handleMediaSelection(this.mediaSelectionProp)
    this.handleTriggerPropertyAction()
    this.setPopIdSummary()
    this.$nextTick(_ => {
      this.$utils.updatePageComponentStyle({ name: 'mediaMenu', ledPage: this.accessorComponent === 'ledPage' })
    })
  }
}
